import { useCallback, useRef, useState } from "react";
import { Box, Card, CardHeader, Stack, Switch, Typography } from "@mui/material";
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import PhoneIcon from '@mui/icons-material/Phone';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { useSelector } from "react-redux";
import { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-toastify";

import { RootState } from "../../store";
import { SystemUserTypes, DefaultRes } from "../../types";
import { api } from "../../http";
import { AuthService } from "../../services/auth.service";
import { RESPONSE_CODES } from "../../constants/responseCodes.constants";

const BRAND_COLOR = '#11A7E2';

interface NotificationOption {
    key: keyof SystemUserTypes.AlertSettings;
    label: string;
    description: string;
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    labelColor?: string;
}

const notificationOptions: NotificationOption[] = [
    {
        key: 'email',
        label: 'Email',
        description: 'Receive an email notification.',
        icon: <EmailIcon sx={{ fontSize: 22 }} />,
        iconBg: '#E8F6FD',
        iconColor: BRAND_COLOR,
    },
    {
        key: 'sms',
        label: 'Phone SMS',
        description: 'Receive a text message to your registered phone number.',
        icon: <SmsIcon sx={{ fontSize: 22 }} />,
        iconBg: '#E8F6FD',
        iconColor: BRAND_COLOR,
    },
    {
        key: 'call',
        label: 'Phone Call',
        description: 'Receive an automated phone call. Use for urgent, immediate awareness.',
        icon: <PhoneIcon sx={{ fontSize: 22 }} />,
        iconBg: '#E8F6FD',
        iconColor: BRAND_COLOR,
    },
    {
        key: 'push',
        label: 'Dashboard Push Notification',
        description: "Receive a real-time notification in the Safety Hub when you're logged in.",
        icon: <NotificationsNoneIcon sx={{ fontSize: 22 }} />,
        iconBg: '#E8F6FD',
        iconColor: BRAND_COLOR,
    },
];

export const SetCustomerAlertSettingsForm = () => {
    const { authUser } = useSelector((state: RootState) => state.auth);
    const [settings, setSettings] = useState(
        authUser?.alertSettings ?? {
            call: false,
            email: false,
            push: false,
            sms: false,
        } as SystemUserTypes.AlertSettings);
    const savingRef = useRef(false);

    const noNotifications = !settings?.email && !settings?.sms && !settings?.call && !settings?.push;

    const saveSettings = useCallback(async (next: SystemUserTypes.AlertSettings) => {
        if (savingRef.current) return;
        savingRef.current = true;
        try {
            const { data: { isValid, result }} = await api.post<
                DefaultRes<SystemUserTypes.Model>,
                AxiosResponse<DefaultRes<SystemUserTypes.Model>>
            >(`/customerWebAppUser/setAlertSettings`, { ...next });

            if (isValid) {
                AuthService.dispatch.updateUser(result);
                toast('Alert trigger settings updated!', { autoClose: 1500, type: 'success' });
            }
        } catch (error: any) {
            let message = 'An error occurred while trying to change alert trigger settings';
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<SystemUserTypes.Model>
                >).response?.data?.message;
                message = RESPONSE_CODES[ERROR_CODE!];
            }
            toast(message, { autoClose: 1500, type: 'error' });
            // Revert on failure
            setSettings(authUser?.alertSettings ?? { call: false, email: false, push: false, sms: false });
        } finally {
            savingRef.current = false;
        }
    }, [authUser?.alertSettings]);

    const handleToggle = (key: keyof SystemUserTypes.AlertSettings) => {
        const next = { ...settings, [key]: !settings[key] };
        setSettings(next);
        saveSettings(next);
    };

    const handleNoNotifications = () => {
        const next = { call: false, email: false, push: false, sms: false } as SystemUserTypes.AlertSettings;
        setSettings(next);
        saveSettings(next);
    };

    return (
        <Card sx={{ border: 1, borderColor: 'divider', boxShadow: (theme) => theme.shadows[2] }}>
            <CardHeader 
                title="Alert Trigger Notifications"
                subheader="How would you like to be notified when a team member triggers an alert?"
                titleTypographyProps={{ fontWeight: 700, fontSize: 20 }}
                subheaderTypographyProps={{ variant: 'body2', sx: { mt: 0.5 } }}
            />
            
            <Stack spacing={1.5} sx={{ px: 2, pb: 2 }}>
                {notificationOptions.map((option) => (
                    <Box
                        key={option.key}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 2,
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                bgcolor: option.iconBg,
                                color: option.iconColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            {option.icon}
                        </Box>

                        <Stack sx={{ flex: 1, minWidth: 0 }}>
                            <Typography fontWeight={600} fontSize={15}>
                                {option.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {option.description}
                            </Typography>
                        </Stack>

                        <Switch
                            checked={settings?.[option.key] ?? false}
                            onChange={() => handleToggle(option.key)}
                            sx={{
                                flexShrink: 0,
                                '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_COLOR },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: BRAND_COLOR },
                            }}
                        />
                    </Box>
                ))}

                {/* No Notifications option */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            bgcolor: '#FDEAEA',
                            color: '#D32F2F',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <NotificationsOffIcon sx={{ fontSize: 22 }} />
                    </Box>

                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={600} fontSize={15} color="error.main">
                            No Notifications
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Do not receive any alert trigger notifications.
                        </Typography>
                    </Stack>

                    <Switch
                        checked={noNotifications}
                        onChange={handleNoNotifications}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>
            </Stack>
        </Card>
    )
}
